import {
	ContextController, Controller, FilterController, FlagController,
	InitOverviewController, InitReviewController, InitTestController,
	SettingsController, TestController, TimerController
} from "@sabyr-dev/mist_core/controller";
import { PqTimerExtras } from "@sabyr-dev/mist_core/lib/timer";
import { Annotations } from "@sabyr-dev/mist_core/lib/userdata";
import {
	DefaultSerializerFactory,
	SerializerFactory,
	StateDataSource
} from "@sabyr-dev/mist_core/model/datasource";
import { GlobalState } from "@sabyr-dev/mist_core/model/datasource/state";
import {
	GlobalStateInitializer
} from "@sabyr-dev/mist_core/model/datasource/stateinitializer";
import {
	Attempt, Product, Question, Quiz,
} from "@sabyr-dev/mist_elibrary/datasource";
import UserData from "@sabyr-dev/mist_core/lib/userdata";
import { Config as AppConfig, PqApp } from "@sabyr-dev/mist_core/app";
import { AcademyNavigationController } from "./controllers";
import {
	AcademyAttemptState, AcademyAttemptStateInitializer,
	AcademyDataSourceFactory
} from "./model/datasource";
import {
	AcademyCommonAttemptStateModel, AcademyCommonGlobalStateModel,
	AcademyCommonListModel, AcademyCommonSettingsModel,
	AcademyOverviewMetadataModel, AcademyOverviewScoreModel,
	AcademyReviewItemModel, AcademyReviewMetadataModel, AcademyTestItemModel,
	AcademyTestMetadataModel
} from "./model";
import AcademyModel from "./model/model";
import AcademyView, {
	AcademyCommonListView, AcademyCommonSettingsView, AcademyOverviewChartView,
	AcademyOverviewSectionView, AcademyReviewHeaderView, AcademyReviewItemView,
	AcademyReviewScoreView, AcademyTestItemView
} from "./view";
import { AcademyFrontendFactory } from "./view/frontend";

export interface Config extends AppConfig {
	hasEmail: boolean;
	freeQuestions?: number | null;
	color: string;
	review?: boolean;
	contentCookie: string;
}

// @ts-ignore
export class AcademyApp extends PqApp<Product, Attempt, Quiz, Question> {
	model: AcademyModel;
	view: AcademyView;
	config: Config;

	serialize(indices: string[]): { attempt: AcademyAttemptState, global: GlobalState } {
		const state = super.serialize(indices) as { attempt: AcademyAttemptState, global: GlobalState };
		const {userData, ...attemptDefaults} = this.model.state.attempt.datasource.initializer.getInitialState(
			state.attempt, this.config.timer
		) as AcademyAttemptState;
		return {
			...state,
			attempt: {
				...state.attempt,
				userData: Object.entries(this.userdata.userdata)
					.reduce((result, [index, annotation]) => ({
						...result,
						[indices[index]]: annotation
					}), {} as Record<Question, Annotations>),
				...attemptDefaults,
			}
		};
	}

	static constructOverview(
		datasource: AcademyDataSourceFactory,
		frontend: AcademyFrontendFactory,
		config: Config, serializer?: SerializerFactory<Product, Attempt, Quiz, Question>,
		controllers?: Partial<{
			init: InitOverviewController<Product, Attempt, Quiz, Question>;
		}>,
		extra_controllers?: Controller<Product, Attempt, Quiz, Question>[],
		libs?: Partial<{
			userdata: UserData<Product, Attempt, Quiz, Question>
		}>
	): AcademyApp {
		serializer ??= new DefaultSerializerFactory();
		const productSerializer = serializer.productSerializer();
		const quizSerializer = serializer.quizSerializer();
		const itemSerializer = serializer.itemSerializer();
		const pq_app = <AcademyApp>super.constructOverview(
			datasource, frontend, config, serializer, controllers, extra_controllers, libs
		)
		pq_app.model = new AcademyModel({
			...pq_app.model,
			metadata: new AcademyOverviewMetadataModel(datasource.metadataDataSource()),
			score: new AcademyOverviewScoreModel(datasource.scoreDataSource(serializer.quizSerializer())),
		});
		pq_app.view = new AcademyView({
			...pq_app.view,
			chart: new AcademyOverviewChartView(frontend.chartFrontend(productSerializer)),
			list: new AcademyCommonListView(frontend.listFrontend(serializer.itemSerializer())),
			settings: new AcademyCommonSettingsView(frontend.settingsFrontend(serializer.itemSerializer())),
			sections: new AcademyOverviewSectionView(frontend.sectionFrontend(quizSerializer, itemSerializer)),
		}, pq_app.view.bindTarget);
		return pq_app;
	};

	static constructTest(
		datasource: AcademyDataSourceFactory,
		frontend: AcademyFrontendFactory,
		config: Config,
		serializer?: SerializerFactory<Product, Attempt, Quiz, Question>,
		controllers?: Partial<{
			init: InitTestController<Product, Attempt, Quiz, Question>,
			test: TestController<Product, Attempt, Quiz, Question>,
			navigation: AcademyNavigationController,
			timer: TimerController<Product, Attempt, Quiz, Question, PqTimerExtras>,
			context: ContextController<Product, Attempt, Quiz, Question>,
			filter: FilterController<Product, Attempt, Quiz, Question>,
			settings: SettingsController<Product, Attempt, Quiz, Question>,
			flag: FlagController<Product, Attempt, Quiz, Question>,
		}>,
		extra_controllers?: Controller<Product, Attempt, Quiz, Question>[],
		libs?: Partial<{
			userdata: UserData<Product, Attempt, Quiz, Question>
		}>
	): AcademyApp {
		serializer ??= new DefaultSerializerFactory();
		const itemSerializer = serializer.itemSerializer();
		const pq_app = <AcademyApp>super.constructTest(
			datasource, frontend, config, serializer, controllers, extra_controllers, libs
		);
		pq_app.model = new AcademyModel({
			...pq_app.model,
			metadata: new AcademyTestMetadataModel(datasource.metadataDataSource()),
			list: new AcademyCommonListModel(datasource.listDataSource(itemSerializer)),
			item: new AcademyTestItemModel(datasource.itemDataSource()),
			settings: new AcademyCommonSettingsModel(datasource.settingsDataSource()),
			state: {
				global: new AcademyCommonGlobalStateModel(datasource.globalStateDataSource(
					datasource.globalStateInitializer() ?? new GlobalStateInitializer()
				)),
				attempt: new AcademyCommonAttemptStateModel(<StateDataSource<AcademyAttemptState>>datasource.attemptStateDataSource(
					datasource.attemptStateInitializer() ?? new AcademyAttemptStateInitializer()
				))
			}
		})
		pq_app.view = new AcademyView({
			...pq_app.view,
			list: new AcademyCommonListView(frontend.listFrontend(itemSerializer)),
			item: new AcademyTestItemView(frontend.itemFrontend(itemSerializer)),
			settings: new AcademyCommonSettingsView(frontend.settingsFrontend(itemSerializer)),
		}, pq_app.view.bindTarget);
		return pq_app;
	}

	static constructReview(
		datasource: AcademyDataSourceFactory,
		frontend: AcademyFrontendFactory,
		config: Config,
		serializer?: SerializerFactory<Product, Attempt, Quiz, Question>,
		controllers?: Partial<{
			init: InitReviewController<Product, Attempt, Quiz, Question>,
			navigation: AcademyNavigationController,
			timer: TimerController<Product, Attempt, Quiz, Question, PqTimerExtras>,
			context: ContextController<Product, Attempt, Quiz, Question>,
			filter: FilterController<Product, Attempt, Quiz, Question>,
			settings: SettingsController<Product, Attempt, Quiz, Question>,
			flag: FlagController<Product, Attempt, Quiz, Question>,
		}>,
		extra_controllers?: Controller<Product, Attempt, Quiz, Question>[],
		libs?: Partial<{
			userdata: UserData<Product, Attempt, Quiz, Question>
		}>
	): AcademyApp {
		serializer ??= new DefaultSerializerFactory();
		const itemSerializer = serializer.itemSerializer();
		const pq_app = <AcademyApp>super.constructReview(
			datasource, frontend, config, serializer, controllers, extra_controllers, libs
		);
		pq_app.model = new AcademyModel({
			...pq_app.model,
			metadata: new AcademyReviewMetadataModel(datasource.metadataDataSource()),
			list: new AcademyCommonListModel(datasource.listDataSource(itemSerializer)),
			item: new AcademyReviewItemModel(datasource.itemDataSource()),
			settings: new AcademyCommonSettingsModel(datasource.settingsDataSource()),
			state: {
				global: new AcademyCommonGlobalStateModel(datasource.globalStateDataSource(
					datasource.globalStateInitializer() ?? new GlobalStateInitializer()
				)),
				// @ts-ignore
				attempt: new AcademyCommonAttemptStateModel(datasource.attemptStateDataSource(
					datasource.attemptStateInitializer() ?? new AcademyAttemptStateInitializer()
				))
			}
		})
		pq_app.view = new AcademyView({
			...pq_app.view,
			header: new AcademyReviewHeaderView(frontend.headerFrontend(itemSerializer)),
			item: new AcademyReviewItemView(frontend.itemFrontend(itemSerializer)),
			list: new AcademyCommonListView(frontend.listFrontend(itemSerializer)),
			scores: new AcademyReviewScoreView(frontend.scoreFrontend()),
			settings: new AcademyCommonSettingsView(frontend.settingsFrontend(itemSerializer)),
		}, pq_app.view.bindTarget);
		return pq_app;
	}
}
